import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, euro, formatApiError } from "../lib/api";
import { C, btnPrimary, softShadow } from "../lib/theme";
import { toast } from "sonner";
import { CreditCard, Clock3, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentPage() {
  const { token } = useParams();
  const [res, setRes] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/reservations/pay/${token}`).then((r) => setRes(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { reservation_id: res.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  const Wrap = ({ children }) => (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 sm:p-10 max-w-md w-full" style={softShadow} data-testid="payment-page">{children}</motion.div>
    </div>
  );

  if (notFound) return <Wrap><div className="text-center"><XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" /><h1 className="font-display text-2xl font-extrabold mb-2" style={{ color: C.INK }}>Prenotazione non trovata</h1><Link to="/" className="font-bold hover:underline" style={{ color: C.CORAL }}>Torna alla home</Link></div></Wrap>;
  if (!res) return <div className="min-h-[50vh] flex items-center justify-center font-semibold" style={{ color: C.CORAL }}>Caricamento…</div>;

  const rows = (
    <div className="rounded-2xl overflow-hidden mb-6 border" style={{ borderColor: C.BORDER }}>
      {[["Ristorante", res.restaurant_name], ["Data", res.date], ["Orario", res.time], ["Persone", `${res.party_size}`], ["A nome di", res.customer_name]].map(([k, v]) => (
        <div key={k} className="flex justify-between px-5 py-3.5 border-b last:border-0" style={{ borderColor: "#f2ecdd" }}><span className="text-sm font-semibold" style={{ color: C.MUTED }}>{k}</span><span className="text-sm font-extrabold" style={{ color: C.INK }}>{v}</span></div>
      ))}
    </div>
  );

  if (res.status === "confirmed" || res.payment_status === "paid") {
    return <Wrap><div className="text-center"><CheckCircle2 className="w-16 h-16 mx-auto mb-5" style={{ color: C.GREEN }} /><h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: C.INK }}>Prenotazione confermata</h1><p className="mb-6" style={{ color: "#6b665d" }}>La caparra è stata versata. Il tuo tavolo è assicurato.</p></div>{rows}</Wrap>;
  }
  if (res.status === "requested") {
    return <Wrap><div className="text-center"><Clock3 className="w-16 h-16 mx-auto mb-5" style={{ color: C.SUN }} /><h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: C.INK }}>In attesa di conferma</h1><p className="mb-6" style={{ color: "#6b665d" }}>Il ristorante deve ancora confermare la disponibilità. Riceverai un'email con il link di pagamento appena sarà pronta.</p></div>{rows}</Wrap>;
  }
  if (res.status === "expired") {
    return <Wrap><div className="text-center"><XCircle className="w-16 h-16 mx-auto mb-5" style={{ color: C.SUN }} /><h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: C.INK }}>Link scaduto</h1><p className="mb-6" style={{ color: "#6b665d" }}>Il tempo per pagare la caparra è scaduto e il posto è stato liberato. Contatta il ristorante per una nuova richiesta.</p></div>{rows}</Wrap>;
  }
  if (res.status === "declined" || res.status === "cancelled") {
    return <Wrap><div className="text-center"><XCircle className="w-16 h-16 text-red-500 mx-auto mb-5" /><h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: C.INK }}>Richiesta non disponibile</h1><p className="mb-6" style={{ color: "#6b665d" }}>Purtroppo questa prenotazione non è più disponibile.</p></div>{rows}</Wrap>;
  }

  // awaiting_payment
  return (
    <Wrap>
      <div className="flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5" style={{ color: C.CORAL }} /><h1 className="font-display text-2xl font-extrabold" style={{ color: C.INK }}>Paga la caparra</h1></div>
      <p className="text-sm mb-6" style={{ color: "#6b665d" }}>Il ristorante ha confermato la disponibilità. Versa la caparra per confermare il tavolo.</p>
      {rows}
      <div className="rounded-2xl p-5 flex items-center justify-between mb-6" style={{ background: "#E3F1EB" }}>
        <span className="text-sm font-semibold" style={{ color: C.GREEN }}>Caparra da versare</span>
        <span data-testid="pay-amount" className="font-display text-3xl font-extrabold" style={{ color: C.GREEN }}>{euro(res.deposit_amount)}</span>
      </div>
      <button data-testid="pay-btn" onClick={pay} disabled={paying} className={`${btnPrimary} w-full`}>
        <CreditCard className="w-4 h-4" /> {paying ? "Reindirizzamento…" : `Paga ${euro(res.deposit_amount)}`}
      </button>
    </Wrap>
  );
}
